import {
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyDropdown,
  SkyMessageBar,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  ADMIN_NEEDS_SSO_REASON,
  MFA_ADMIN_ROLES,
  MFA_MODES,
  MFA_MODE_SETTING_KEY,
  mfaModeCovers,
  strictestMfaMode,
  type MfaMode,
  type MfaPolicyView,
} from "@shared/types/mfa";
import { ReauthDialog } from "@/components/settings/ReauthDialog";
import { useReauthRetry } from "@/hooks/use-group-access";
import { ME_QUERY_KEY } from "@/hooks/use-me";
import { PLATFORM_SETTINGS_PATH, PLATFORM_SETTINGS_QUERY_KEY } from "@/hooks/use-platform-settings";
import { apiRequest } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { ErrorState } from "./ScreenState";

export const MFA_POLICY_PATH = "/api/v2/platform/auth/mfa";

/** One line per mode, in the reader's terms (design 4.3). */
export const MFA_MODE_TEXT: Record<MfaMode, { label: string; help: string }> = {
  off: { label: "Off", help: "Nobody is asked for a second sign-in step." },
  privileged: {
    label: "Privileged actions",
    help: "Anyone changing access, a destination or a security setting needs a second sign-in step.",
  },
  admins: {
    label: "Admins",
    help: "Platform, tenant and instance admins need a second sign-in step for everything, so they sign in with single sign-on. Everyone needs it for privileged actions.",
  },
  all: { label: "Everyone", help: "Everyone needs a second sign-in step for everything." },
};

/**
 * Platform settings > General > Multi-factor sign-in (admin MFA, design 4.3).
 *
 * The mode is one key (`auth.mfa_mode`) with its own save, beside the page's numeric save bar,
 * because it answers with more than a number: the deployment floor (`AUTH_MFA_FLOOR`, which this
 * screen cannot lower), and which configured sign-in providers can prove a second factor. The
 * save itself is a privileged write: it needs a recent sign-in that proved MFA when a mode is on.
 */
/**
 * #1712: once a mode covers admins, an admin on a sign-in that cannot prove a second step is
 * refused every admin action. Said before the save, with how many people it would stop.
 */
export function adminsWithoutSsoWarning(count: number): string {
  const who =
    count === 1
      ? "1 person holds an admin role on a sign-in that cannot prove a second step."
      : `${count} people hold an admin role on a sign-in that cannot prove a second step.`;
  return `${ADMIN_NEEDS_SSO_REASON} ${who} With this mode they cannot take admin actions until their sign-in is reset on the Users page, Platform tab.`;
}

export function MfaSettingsCard() {
  const client = useQueryClient();
  const notice = useNotice();
  const saveError = useInlineError();
  const retry = useReauthRetry();
  const policy = useQuery<MfaPolicyView>({ queryKey: [MFA_POLICY_PATH] });
  const [draft, setDraft] = useState<MfaMode | null>(null);
  const save = useMutation({
    mutationFn: (mode: MfaMode) =>
      apiRequest(`${PLATFORM_SETTINGS_PATH}/${MFA_MODE_SETTING_KEY}`, {
        method: "PUT",
        body: { value: mode },
      }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: [MFA_POLICY_PATH] }),
        client.invalidateQueries({ queryKey: PLATFORM_SETTINGS_QUERY_KEY }),
        client.invalidateQueries({ queryKey: ME_QUERY_KEY }),
      ]);
    },
  });

  if (policy.isError) {
    return (
      <ErrorState
        title="Multi-factor sign-in did not load"
        error={policy.error}
        onRetry={() => void policy.refetch()}
        testId="mfa-settings-error"
      />
    );
  }
  const view = policy.data;
  const selected = draft ?? view?.setting ?? "off";
  const effective = view ? strictestMfaMode(view.floor, selected) : selected;
  const floorWins = view !== undefined && view.floor !== "off" && effective === view.floor;
  const anyCanProve = view?.issuers.some((issuer) => issuer.canProveMfa) ?? false;
  const dirty = view !== undefined && draft !== null && draft !== view.setting;

  function handleSave() {
    if (!dirty || draft === null) return;
    saveError.clear();
    const mode = draft;
    const saved = () => {
      setDraft(null);
      notice.success(`Multi-factor sign-in is ${MFA_MODE_TEXT[mode].label.toLowerCase()}.`, {
        title: "Setting saved",
      });
    };
    retry
      .run(() => save.mutateAsync(mode), saved)
      .then((result) => {
        if (result === null) return;
        saved();
      })
      .catch((cause: unknown) => saveError.show(cause, "The setting was not saved."));
  }

  return (
    <SkyCard appearance="outline" testId="mfa-settings">
      <div className="flex flex-col gap-4 p-1">
        <div>
          <SkyText as="h2" size={400} weight="semibold" block>
            Multi-factor sign-in
          </SkyText>
          <SkyText size={200} color="muted" block>
            Who must complete a second sign-in step, such as a code sent to their email. The sign-in
            provider asks for it. skyData checks the proof on every request the mode covers.
          </SkyText>
        </div>

        <SkyDropdown
          label="Required for"
          testId="mfa-mode"
          selectedOptions={[selected]}
          disabled={!view || save.isPending}
          options={MFA_MODES.map((mode) => ({
            value: mode,
            text: `${MFA_MODE_TEXT[mode].label}: ${MFA_MODE_TEXT[mode].help}`,
          }))}
          onOptionSelect={(values) => {
            const next = values[0] as MfaMode | undefined;
            if (next) setDraft(next);
          }}
        />

        {view && mfaModeCovers(effective, MFA_ADMIN_ROLES) && (view.adminsWithoutSso ?? 0) > 0 ? (
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="mfa-admins-without-sso"
            message={adminsWithoutSsoWarning(view.adminsWithoutSso ?? 0)}
          />
        ) : null}

        {floorWins && view ? (
          <SkyText size={200} testId="mfa-floor" block>
            Set by the deployment: {MFA_MODE_TEXT[view.floor].label}. This screen cannot lower it.
          </SkyText>
        ) : null}

        <div className="flex flex-col gap-2" data-testid="mfa-issuers">
          <SkyText size={300} weight="semibold" block>
            Sign-in providers
          </SkyText>
          {(view?.issuers ?? []).map((issuer) => (
            <div
              key={issuer.name ?? issuer.claims}
              className="flex items-center gap-2"
              data-testid={`mfa-issuer-${issuer.name ?? issuer.claims}`}
            >
              <SkyText size={200}>{issuer.name ?? issuer.claims}</SkyText>
              <SkyBadge appearance="tint" color={issuer.canProveMfa ? "success" : "warning"}>
                {issuer.canProveMfa ? "Can prove MFA" : "Cannot prove MFA"}
              </SkyBadge>
            </div>
          ))}
          {view && !anyCanProve ? (
            <SkyText size={200} color="muted" block testId="mfa-no-issuer">
              {/* Walk W21: no repository path in product copy; the claims proof is the operators'
                  runbook (docs/auth/entra-external-id.md, "Multi-factor sign-in"). */}
              No provider can prove a second step yet, so only Off can be saved. A provider is
              marked once Skypoint has confirmed its tokens carry the second step.
            </SkyText>
          ) : null}
        </div>

        {view && effective !== "off" ? (
          <SkyText size={200} color="muted" block testId="mfa-limits-line">
            A browser is remembered for {view.rememberDays} days after a second step when the person
            asks. An admin signs in again at least every {view.adminSessionMaxHours} hours. Both are
            set under Limits.
          </SkyText>
        ) : null}

        <InlineErrorBar
          error={saveError.error}
          title="The setting was not saved"
          testId="mfa-save-error"
        />

        <div>
          <SkyButton
            appearance="primary"
            disabled={!dirty}
            loading={save.isPending}
            onClick={handleSave}
            testId="mfa-save"
          >
            Save multi-factor sign-in
          </SkyButton>
        </div>
      </div>

      <ReauthDialog
        open={retry.hold.isOpen}
        isRetrying={retry.hold.isRetrying}
        error={retry.hold.error}
        onConfirm={retry.hold.confirm}
        onCancel={retry.hold.cancel}
        title="Confirm your identity to change multi-factor sign-in"
        description="Your session is still valid, but changing a security setting needs a fresh sign-in."
        heldMessage="Your change is held. If signing in again takes you to your sign-in page, make the change again when you are back."
        footnote="Nothing has changed yet. Cancelling leaves the setting as it is."
        code={retry.hold.code}
        blocked={retry.hold.blocked}
        remember={retry.hold.remember}
      />
    </SkyCard>
  );
}
